import type {Component, Editor} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {findCountdownComponents, fitCountdownsToCanvas, isCountdownComponent, updateCountdownDigitsInDom} from '@EmailMakerReact/lib/email-maker-countdown-library';
import {COUNTDOWN_TICK_INTERVAL_MS} from '@EmailMakerReact/lib/email-maker-countdown-library.types';

export function initCountdownCanvasSync(editor: Editor): () => void {
  const teardowns = [initDigitTicker(editor), initFitToCanvas(editor)];

  return (): void => {
    teardowns.forEach((teardown) => {
      teardown();
    });
  };
}

function initDigitTicker(editor: Editor): () => void {
  let intervalId: number | null = null;

  const stop = (): void => {
    if (intervalId !== null) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
  };

  const tick = (): void => {
    const countdowns = findCountdownComponents(editor);
    if (countdowns.length === 0) {
      stop();
      return;
    }
    if (document.hidden) {
      return;
    }
    countdowns.forEach(updateCountdownDigitsInDom);
  };

  const start = (): void => {
    intervalId ??= window.setInterval(tick, COUNTDOWN_TICK_INTERVAL_MS);
  };

  const onComponentMount = (component: Component): void => {
    if (isCountdownComponent(component)) {
      start();
    }
  };

  editor.on('component:mount', onComponentMount);
  editor.on('load', start);
  editor.on('undo redo', tick);

  return (): void => {
    stop();
    editor.off('component:mount', onComponentMount);
    editor.off('load', start);
    editor.off('undo redo', tick);
  };
}

function initFitToCanvas(editor: Editor): () => void {
  let bodyObserver: ResizeObserver | null = null;
  let fitFrame: number | null = null;

  const fitAll = (): void => {
    fitFrame = null;
    fitCountdownsToCanvas(editor);
  };

  const scheduleFitAll = (): void => {
    fitFrame ??= window.requestAnimationFrame(fitAll);
  };

  const onCountdownOrPartChanged = (component: Component): void => {
    const isPartOfCountdown = component.closestType(EMAIL_MAKER_COMPONENT_TYPES.misc.PMW_COUNTDOWN) !== undefined;
    if (isCountdownComponent(component) || isPartOfCountdown) {
      scheduleFitAll();
    }
  };

  const observeCanvasBody = (): void => {
    bodyObserver?.disconnect();
    const body = editor.Canvas.getBody();
    if (!body) {
      return;
    }
    bodyObserver = new ResizeObserver(scheduleFitAll);
    bodyObserver.observe(body);
  };

  editor.on('load', observeCanvasBody);
  editor.on('canvas:frame:load:body', observeCanvasBody);
  editor.on('component:mount', onCountdownOrPartChanged);
  editor.on('component:styleUpdate', onCountdownOrPartChanged);
  editor.on('undo redo', scheduleFitAll);

  return (): void => {
    bodyObserver?.disconnect();
    if (fitFrame !== null) {
      window.cancelAnimationFrame(fitFrame);
    }
    editor.off('load', observeCanvasBody);
    editor.off('canvas:frame:load:body', observeCanvasBody);
    editor.off('component:mount', onCountdownOrPartChanged);
    editor.off('component:styleUpdate', onCountdownOrPartChanged);
    editor.off('undo redo', scheduleFitAll);
  };
}
